import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShoppingCart, PackageCheck, Truck, RefreshCw } from "lucide-react";
import { api, fmtMoney } from "@/lib/api";

const STATUS_META = {
  needs_ordering: { label: "🛒 Needs ordering", cls: "bg-red-50 border-[#DC2626] text-[#DC2626]" },
  ordered: { label: "⏳ Ordered from supplier", cls: "bg-amber-50 border-[#FFC220] text-[#C99700]" },
  arrived: { label: "✅ Arrived at warehouse", cls: "bg-green-50 border-green-600 text-green-700" },
};

export default function AdminSupplierOrders() {
  const [orders, setOrders] = useState(null);

  const load = useCallback(() => {
    api.get("/orders/admin/supplier-queue").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (orderId, status) => {
    try {
      await api.patch(`/orders/admin/${orderId}/supplier-status?status=${status}`);
      toast.success(`Marked as ${status.replace("_", " ")}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  if (orders === null) return <div className="ipv-eyebrow">Loading…</div>;

  const active = orders.filter((o) => o.supplier_status !== "arrived");
  const done = orders.filter((o) => o.supplier_status === "arrived");

  const OrderCard = ({ o }) => {
    const meta = STATUS_META[o.supplier_status] || STATUS_META.needs_ordering;
    const special = (o.line_items || []).filter((li) => li.fulfillment_type === "order_in");
    return (
      <div className="border border-gray-200 bg-white p-5" data-testid={`supplier-order-${o.order_number}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="font-display font-black">{o.order_number}</div>
            <div className="text-xs text-gray-500 mt-0.5">{new Date(o.created_at).toLocaleString()} · paid {fmtMoney(o.total)}</div>
          </div>
          <span className={`border-2 px-3 py-1 text-xs font-bold ${meta.cls}`}>{meta.label}</span>
        </div>

        <div className="mt-4 space-y-1">
          {special.map((li, i) => (
            <div key={i} className="text-sm font-bold">
              {li.quantity}× {li.name}{li.color ? ` — ${li.color}` : ""}
              <span className="text-xs text-gray-500 font-normal ml-2">~{li.lead_time_days || 7} days</span>
            </div>
          ))}
        </div>

        <div className="mt-4 bg-gray-50 border border-gray-200 p-3 text-sm">
          <div className="font-bold text-xs uppercase tracking-wider text-gray-500 mb-1">
            {o.fulfillment === "delivery" ? "🚚 Ship / deliver to" : "🏬 In-store pickup"}
          </div>
          <div>{o.customer_name} · {o.customer_phone || "no phone"} · {o.customer_email}</div>
          {o.fulfillment === "delivery" && o.delivery_address && (
            <div className="mt-1 font-semibold select-all">{o.delivery_address}</div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {o.supplier_status !== "ordered" && o.supplier_status !== "arrived" && (
            <button onClick={() => setStatus(o.id, "ordered")} className="bg-[#FFC220] text-black text-xs font-bold px-4 py-2 hover:bg-[#e6ae1c]" data-testid={`mark-ordered-${o.order_number}`}>
              Mark ORDERED from supplier
            </button>
          )}
          {o.supplier_status === "ordered" && (
            <button onClick={() => setStatus(o.id, "arrived")} className="bg-green-600 text-white text-xs font-bold px-4 py-2 hover:bg-green-700" data-testid={`mark-arrived-${o.order_number}`}>
              Mark ARRIVED at warehouse
            </button>
          )}
          {o.supplier_status === "arrived" && (
            <span className="text-xs text-gray-500 self-center">→ Now set the order status in Orders ("Ready for pickup" / "Out for delivery")</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="admin-supplier-orders">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tighter">Supplier Orders</h1>
          <p className="text-gray-600 text-sm mt-1">Special-order items customers already paid for — buy these from Walmart/Faire/CJ with your Square Card.</p>
        </div>
        <button onClick={load} className="btn-outline inline-flex items-center gap-2 text-sm" data-testid="supplier-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {active.length === 0 && done.length === 0 && (
        <div className="border border-gray-200 bg-white p-12 text-center text-gray-500" data-testid="supplier-empty">
          <ShoppingCart className="w-8 h-8 mx-auto text-gray-300" />
          <div className="mt-3 font-bold">No supplier orders yet</div>
          <div className="text-sm mt-1">When a customer buys a Special Order item, it appears here and you get a WhatsApp alert.</div>
        </div>
      )}

      <div className="space-y-4">{active.map((o) => <OrderCard key={o.id} o={o} />)}</div>

      {done.length > 0 && (
        <>
          <div className="ipv-eyebrow mt-10 mb-4">Arrived ({done.length})</div>
          <div className="space-y-4 opacity-70">{done.map((o) => <OrderCard key={o.id} o={o} />)}</div>
        </>
      )}
    </div>
  );
}
